import React, { useEffect, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import API from "../api";
import jsPDF from "jspdf";
import BackButton from "./common/BackButton";
import { SEMANTIC } from "../styles/theme";
import { useAppModal } from "../hooks/useAppModal";

// ============================================================
// Chart.js registration
// ============================================================
ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

// ============================================================
// Base chart options (default)
// ============================================================
const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
      labels: { color: "white" },
    },
    datalabels: {
      display: false,
    },
  },
  scales: {
    x: {
      ticks: { color: "white" },
      grid: { color: "rgba(255,255,255,0.2)" },
    },
    y: {
      ticks: { color: "white" },
      grid: { color: "rgba(255,255,255,0.2)" },
    },
  },
};

// ============================================================
// Colori ciclici portafogli
// ============================================================
const COLORS = [
  "#ff3b3b",
  "#3b82f6",
  "#f59e0b",
  "#10b981",
  "#a855f7",
  "#ec4899",
];

// ============================================================
// Dataset linea portafoglio
// ============================================================
const buildPortfolioDataset = (portfolio, color, todayIndex) => ({
  label: portfolio.label,
  data: portfolio.values,
  borderColor: color,
  borderWidth: 3,
  tension: 0.25,
  pointRadius: 0,
  spanGaps: false,
  segment: todayIndex != null ? {
    borderDash: (ctx) => ctx.p0DataIndex >= todayIndex - 1 ? [6, 4] : undefined,
    borderColor: (ctx) => ctx.p0DataIndex >= todayIndex - 1 ? `${color}99` : color,
  } : undefined,
});

// ============================================================
// Dataset marker (F / W)
// ============================================================
const buildEventDataset = ({ label, points, letter, offsetFactor }) => ({
  label,
  data: points.map((p) => ({
    x: p.x,
    y: p.y * offsetFactor,
  })),
  showLine: false,
  pointRadius: 6,
  pointHoverRadius: 6,
  pointBackgroundColor: "transparent",
  pointBorderColor: "#ffffff",
  pointBorderWidth: 1,
  datalabels: {
    display: true,
    formatter: () => letter,
    color: "#ffffff",
    font: { weight: "bold", size: 9 },
    align: "center",
    anchor: "center",
  },
});

// ============================================================
// MAIN COMPONENT
// ============================================================
const resolveCssVar = (v) =>
  v.startsWith("var(")
    ? getComputedStyle(document.documentElement)
        .getPropertyValue(v.slice(4, -1).trim())
        .trim()
    : v;

const Performance = () => {
  const [performance, setPerformance] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const userId = localStorage.getItem("userId");
  const { t } = useTranslation();
  const { modal, showModal } = useAppModal();

  // Modalità: paper / real (da ?mode=, altrimenti dal profilo utente)
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get("mode");
  const [mode, setMode] = useState(urlMode === "real" || urlMode === "paper" ? urlMode : null);
  useEffect(() => {
    if (mode) return;
    API.get(`/users/${userId}/profile`)
      .then((r) => setMode(r.data.trading_mode === "real" ? "real" : "paper"))
      .catch(() => setMode("paper"));
  }, [mode, userId]);

  const colorBuy  = resolveCssVar(SEMANTIC.actionBuy);
  const colorSell = resolveCssVar(SEMANTIC.actionSell);

  // ============================================================
  // LOAD PERFORMANCE + PORTFOLIOS
  // ============================================================
  const loadPerformance = useCallback(async () => {
    if (!mode) return;
    setPerformance(null);
    setError("");
    try {
      const res = await API.get(`/performance/daily/${userId}`, { params: { trading_mode: mode } });
      const data = res.data;

      if (!data || !data.dates || data.dates.length === 0) {
        setError(t("performance.no_data"));
        return;
      }

      setPerformance(data);
    } catch (err) {
      console.error("Performance load error:", err);
      setError(t("performance.error_load"));
    }
  }, [userId, t, mode]);

  const loadPortfolios = useCallback(async () => {
    if (!mode) return;
    try {
      const res = await API.get(`/portfolios/${userId}`);
      const list = (res.data.portfolios || []).filter(
        p => p.status !== 'closed' && p.status !== 'closing' && (p.trading_mode || 'paper') === mode
      );
      const enriched = await Promise.all(
        list.map(async (p) => {
          try {
            const tr = await API.get(`/portfolio/${p.id}/tickers`);
            return { ...p, tickers: tr.data.tickers || [] };
          } catch {
            return { ...p, tickers: [] };
          }
        })
      );
      setPortfolios(enriched);
    } catch (err) {
      console.error("Portfolios load error:", err);
    }
  }, [userId, mode]);

  useEffect(() => {
    loadPerformance();
    loadPortfolios();
  }, [loadPerformance, loadPortfolios]);

  // ============================================================
  // GENERA PDF — restituisce { dataUri, base64 }
  // ============================================================
  const buildPdf = () => {
    const getChartData = (boxId) => {
      const canvas = document.querySelector(`#${boxId} canvas`);
      if (!canvas) return null;
      return { img: canvas.toDataURL("image/png", 1.0), aspect: canvas.height / canvas.width };
    };
    const c1 = getChartData("total-chart-box");
    const c2 = getChartData("return-chart-box");
    const c3 = getChartData("comparison-chart-box");
    const c4 = getChartData("sharpe-chart-box");

    const pdf = new jsPDF("portrait", "mm", "a4");
    const PW  = 210;
    const PH  = 297;
    const M   = 12;   // margine esterno
    const PAD = 3;    // padding interno riquadri grafico
    const GAP = 4;    // gap tra celle
    const W   = PW - M * 2;

    const now = new Date();
    const exportedAt = now.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });

    // sfondo bianco
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, PW, PH, "F");

    let y = M;

    // ── header ─────────────────────────────────────────────
    pdf.setTextColor(30, 30, 30);
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text(`Portfolio Dashboard — ${mode === "real" ? "Real Trading" : "Paper Trading"}`, M, y + 5);
    pdf.setTextColor(130, 130, 130);
    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Esportato il: ${exportedAt}`, PW - M, y + 5, { align: "right" });
    y += 8;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(M, y, PW - M, y);
    y += 4;

    // ── tabella portafogli ─────────────────────────────────
    const lastIdx = performance.total.length - 1;
    const lastNI  = performance.net_invested?.[lastIdx] || 0;
    const lastVal = performance.total[lastIdx] || 0;
    const totalRet = lastNI > 0 ? (((lastVal - lastNI) / lastNI) * 100).toFixed(2) : "N/A";

    const COL   = { name: M + 2, val: M + 66, inv: M + 97, ret: M + 128, tickers: M + 152 };
    const ROW_H = 5.5;

    pdf.setFillColor(240, 240, 240);
    pdf.rect(M, y, W, ROW_H, "F");
    pdf.setTextColor(80, 80, 80);
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.text("Portafoglio", COL.name,    y + 3.5);
    pdf.text("Valore",      COL.val,     y + 3.5);
    pdf.text("Investito",   COL.inv,     y + 3.5);
    pdf.text("Rend. %",     COL.ret,     y + 3.5);
    pdf.text("Tickers",     COL.tickers, y + 3.5);
    y += ROW_H;

    pdf.setFillColor(228, 244, 234);
    pdf.rect(M, y, W, ROW_H, "F");
    pdf.setTextColor(30, 30, 30);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.text("TOTALE",                        COL.name, y + 3.5);
    pdf.text(`$${Number(lastVal).toFixed(2)}`, COL.val,  y + 3.5);
    pdf.text(`$${Number(lastNI).toFixed(2)}`,  COL.inv,  y + 3.5);
    const tc = parseFloat(totalRet) >= 0 ? [0, 150, 80] : [200, 50, 50];
    pdf.setTextColor(...tc);
    pdf.text(`${parseFloat(totalRet) >= 0 ? "+" : ""}${totalRet}%`, COL.ret, y + 3.5);
    y += ROW_H;

    pdf.setFont("helvetica", "normal");
    portfolios.forEach((p, i) => {
      pdf.setFillColor(...(i % 2 === 0 ? [255, 255, 255] : [248, 248, 252]));
      pdf.rect(M, y, W, ROW_H, "F");
      const pVal = Number(p.total_balance || 0);
      const pInv = Number(p.net_invested ?? p.budget ?? 0);
      const pRet = pInv > 0 ? (((pVal - pInv) / pInv) * 100).toFixed(2) : "N/A";
      const tickers = (p.tickers || []).map(tk => tk.ticker).join(", ") || "—";
      pdf.setTextColor(50, 50, 50);
      pdf.setFontSize(6.5);
      pdf.text(p.Nome_tipo_modello || `Portfolio ${p.id}`, COL.name, y + 3.5);
      pdf.text(`$${pVal.toFixed(2)}`, COL.val, y + 3.5);
      pdf.text(`$${pInv.toFixed(2)}`, COL.inv, y + 3.5);
      const pc = parseFloat(pRet) >= 0 ? [0, 150, 80] : [200, 50, 50];
      pdf.setTextColor(...pc);
      pdf.text(`${parseFloat(pRet) >= 0 ? "+" : ""}${pRet}%`, COL.ret, y + 3.5);
      pdf.setTextColor(100, 100, 100);
      pdf.text(pdf.splitTextToSize(tickers, W - (COL.tickers - M) - 2)[0], COL.tickers, y + 3.5);
      y += ROW_H;
    });

    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.2);
    pdf.line(M, y, PW - M, y);
    y += 5;

    // ── griglia 2×2 dei grafici ────────────────────────────
    // Lo spazio rimanente viene diviso in 2 righe × 2 colonne.
    const LABEL_H  = 5;   // altezza etichetta sopra ogni riquadro
    const available = PH - y - M;
    const CW = (W - GAP) / 2;                            // larghezza colonna
    const RH = (available - GAP - LABEL_H * 2) / 2;      // altezza riga (spazio netto per la chart)

    // ── helper: disegna cella con canvas ──────────────────────
    const drawChartCell = (data, label, col, row) => {
      if (!data) return;
      const cx   = M + col * (CW + GAP);
      const cy   = y + row * (LABEL_H + RH + GAP);
      const boxY = cy + LABEL_H;
      const imgW = CW - PAD * 2;
      const imgH = Math.min(imgW * data.aspect, RH - PAD * 2);

      pdf.setTextColor(60, 60, 60);
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      pdf.text(label, cx, cy + 3.5);

      pdf.setFillColor(15, 15, 20);
      pdf.setDrawColor(180, 180, 185);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(cx, boxY, CW, RH, 2, 2, "FD");
      pdf.addImage(data.img, "PNG", cx + PAD, boxY + PAD, imgW, imgH, undefined, "FAST");
    };

    drawChartCell(c1, t("performance.total_value"),  0, 0);
    drawChartCell(c2, t("performance.daily_return"),  1, 0);
    drawChartCell(c3, t("performance.comparison"),    0, 1);

    // ── cella Sharpe: canvas se disponibile, altrimenti testo ──
    {
      const col  = 1;
      const row  = 1;
      const cx   = M + col * (CW + GAP);
      const cy   = y + row * (LABEL_H + RH + GAP);
      const boxY = cy + LABEL_H;

      // etichetta cella
      pdf.setTextColor(60, 60, 60);
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      pdf.text("Indice di Sharpe", cx, cy + 3.5);

      // riquadro sfondo scuro
      pdf.setFillColor(13, 13, 34);
      pdf.setDrawColor(180, 180, 185);
      pdf.setLineWidth(0.25);
      pdf.roundedRect(cx, boxY, CW, RH, 2, 2, "FD");

      if (c4) {
        // canvas rolling disponibile → inserisci immagine
        const imgW = CW - PAD * 2;
        const imgH = Math.min(imgW * c4.aspect, RH - PAD * 2);
        pdf.addImage(c4.img, "PNG", cx + PAD, boxY + PAD, imgW, imgH, undefined, "FAST");
      } else {
        // canvas non disponibile → calcola Sharpe da performance.total e disegna testo
        const pTotal = performance.total || [];
        const pRets  = [];
        for (let i = 1; i < pTotal.length; i++) {
          if (pTotal[i] != null && pTotal[i - 1] != null && pTotal[i - 1] !== 0)
            pRets.push((pTotal[i] - pTotal[i - 1]) / pTotal[i - 1]);
        }
        const calcS = (rets) => {
          if (rets.length < 5) return null;
          const mean = rets.reduce((s, r) => s + r, 0) / rets.length;
          const std  = Math.sqrt(rets.reduce((s, r) => s + (r - mean) ** 2, 0) / (rets.length - 1));
          if (std === 0) return null;
          return (mean - (Math.pow(1.04, 1/252) - 1)) / std * Math.sqrt(252);
        };
        const sharpeWindows = [
          { label: "Globale",  v: calcS(pRets) },
          { label: "12 mesi",  v: calcS(pRets.slice(-252)) },
          { label: "6 mesi",   v: calcS(pRets.slice(-126)) },
          { label: "3 mesi",   v: calcS(pRets.slice(-63))  },
        ].filter(w => w.v !== null);

        const sharpeRgb = (v) =>
          v >= 1.5 ? [0, 220, 110] : v >= 0.5 ? [255, 170, 0] : v >= 0 ? [136, 153, 170] : [255, 80, 80];

        const globalS = sharpeWindows[0];
        let ty = boxY + 8;

        if (globalS) {
          // valore principale grande
          const [r, g, b] = sharpeRgb(globalS.v);
          pdf.setTextColor(r, g, b);
          pdf.setFontSize(22);
          pdf.setFont("helvetica", "bold");
          pdf.text(globalS.v.toFixed(3), cx + CW / 2, ty, { align: "center" });
          ty += 7;

          pdf.setTextColor(160, 160, 160);
          pdf.setFontSize(7);
          pdf.setFont("helvetica", "normal");
          const qual = globalS.v >= 1.5 ? "Eccellente" : globalS.v >= 1 ? "Buono" : globalS.v >= 0.5 ? "Accettabile" : globalS.v >= 0 ? "Basso" : "Negativo";
          pdf.text(`Globale · ${qual} · Rf 4%`, cx + CW / 2, ty, { align: "center" });
          ty += 6;

          // separatore
          pdf.setDrawColor(50, 50, 70);
          pdf.setLineWidth(0.2);
          pdf.line(cx + PAD, ty, cx + CW - PAD, ty);
          ty += 4;
        }

        // finestre con barra
        const BAR_MAX = CW - PAD * 2 - 22;
        sharpeWindows.forEach(({ label, v }) => {
          const [r, g, b] = sharpeRgb(v);
          pdf.setTextColor(160, 160, 160);
          pdf.setFontSize(6.5);
          pdf.setFont("helvetica", "normal");
          pdf.text(label, cx + PAD, ty);

          pdf.setTextColor(r, g, b);
          pdf.setFont("helvetica", "bold");
          pdf.text(v.toFixed(3), cx + CW - PAD, ty, { align: "right" });

          // barra proporzionale (max 3)
          const barW = Math.min(BAR_MAX, Math.max(0, (v / 3)) * BAR_MAX);
          pdf.setFillColor(30, 30, 50);
          pdf.roundedRect(cx + PAD, ty + 1, BAR_MAX, 2.5, 0.5, 0.5, "F");
          if (barW > 0) {
            pdf.setFillColor(r, g, b);
            pdf.roundedRect(cx + PAD, ty + 1, barW, 2.5, 0.5, 0.5, "F");
          }
          ty += 8;
        });
      }
    }

    const base64 = pdf.output("datauristring").split(",")[1];
    return { pdf, base64, filename: `portfolio-dashboard-${now.toISOString().slice(0, 10)}.pdf` };
  };

  // ============================================================
  // EXPORT — scarica il PDF
  // ============================================================
  const exportToPDF = async () => {
    setExporting(true);
    try {
      const { pdf, filename } = buildPdf();
      pdf.save(filename);
    } finally {
      setExporting(false);
    }
  };

  // ============================================================
  // SEND — genera il PDF e lo invia via email
  // ============================================================
  const sendDashboard = async () => {
    setSending(true);
    try {
      const { base64 } = buildPdf();
      const res = await API.post(`/performance/send-dashboard/${userId}`, { pdf: base64 });
      showModal({
        type: 'success',
        title: t('performance.send_success_title'),
        message: t('performance.send_success_message', { email: res.data.to }),
      });
    } catch (e) {
      showModal({
        type: 'error',
        title: t('performance.send_error_title'),
        message: e.response?.data?.error || t('performance.send_error_message'),
      });
    } finally {
      setSending(false);
    }
  };

  // ============================================================
  // BUILD EVENT POINTS
  // ============================================================
  const buildEventPoints = (values, events) => {
    const points = [];
    Object.entries(events || {}).forEach(([date, evts]) => {
      const idx = performance.dates.indexOf(date);
      if (idx === -1) return;

      const baseValue = values[idx];
      if (baseValue == null) return;

      if (evts.includes("F")) points.push({ x: date, y: baseValue, type: "F" });
      if (evts.includes("W")) points.push({ x: date, y: baseValue, type: "W" });
    });
    return points;
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
    {modal}
    <div
      style={{
        backgroundColor: SEMANTIC.backgroundApp,
        color: SEMANTIC.textPrimary,
        minHeight: "100vh",
        padding: "20px",
      }}
    >
      <BackButton />

      <h2 className="text-center mb-3">{t("performance.title")}</h2>

      {/* Selettore Paper / Real Trading */}
      <div className="d-flex justify-content-center gap-2 mb-4">
        {[["paper", "🧪 Paper Trading", "#00ff88"], ["real", "🏦 Real Trading", "#00aaff"]].map(([val, lbl, col]) => (
          <button
            key={val}
            onClick={() => setMode(val)}
            style={{
              padding: "8px 20px",
              borderRadius: 20,
              fontSize: 13,
              fontWeight: mode === val ? 700 : 400,
              border: `1px solid ${mode === val ? col : "rgba(255,255,255,0.2)"}`,
              backgroundColor: mode === val ? `${col}22` : "transparent",
              color: mode === val ? col : "#889",
              cursor: "pointer",
            }}
          >{lbl}</button>
        ))}
      </div>
      {error && <p className="alert alert-danger">{error}</p>}

      {/* ================= TOTAL ================= */}
      {performance && (
        <>
          <h4 className="text-center mb-3">{t("performance.total_value")}</h4>
          <div
            id="total-chart-box"
            style={{
              backgroundColor: SEMANTIC.backgroundCard,
              padding: "20px",
              borderRadius: "15px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
              marginBottom: "60px",
              height: "400px",
            }}
          >
            <Line
              data={{
                labels: performance.dates,
                datasets: [
                  {
                    data: performance.total,
                    borderColor: colorBuy,
                    borderWidth: 4,
                    tension: 0.25,
                    pointRadius: 0,
                    segment: performance.today_index != null ? {
                      borderDash: (ctx) => ctx.p0DataIndex >= performance.today_index - 1 ? [6, 4] : undefined,
                      borderColor: (ctx) => ctx.p0DataIndex >= performance.today_index - 1 ? `${colorBuy}99` : colorBuy,
                    } : undefined,
                  },
                  ...(() => {
                    const pts = buildEventPoints(
                      performance.total,
                      performance.total_events
                    );
                    return [
                      buildEventDataset({
                        label: t("performance.event_funding"),
                        points: pts.filter((p) => p.type === "F"),
                        letter: "F",
                        offsetFactor: 1.05,
                      }),
                      buildEventDataset({
                        label: t("performance.event_withdraw"),
                        points: pts.filter((p) => p.type === "W"),
                        letter: "W",
                        offsetFactor: 0.95,
                      }),
                    ];
                  })(),
                ],
              }}
              options={{
                ...baseChartOptions,
                plugins: {
                  ...baseChartOptions.plugins,
                  legend: { display: false },
                },
              }}
            />
          </div>
        </>
      )}

      {/* ================= RENDIMENTO % COMPLESSIVO ================= */}
      {performance && (() => {
        const returnSeries = performance.total.map((val, i) => {
          const invested = performance.net_invested?.[i];
          if (val == null || !invested || invested === 0) return null;
          return parseFloat((((val - invested) / invested) * 100).toFixed(3));
        });

        return (
          <>
            <h4 className="text-center mb-3" style={{ marginTop: "40px" }}>
              {t("performance.daily_return")}
            </h4>
            <div
              id="return-chart-box"
              style={{
                backgroundColor: SEMANTIC.backgroundCard,
                padding: "20px",
                borderRadius: "15px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                marginBottom: "60px",
                height: "300px",
              }}
            >
              <Line
                data={{
                  labels: performance.dates,
                  datasets: [
                    {
                      label: "Portafoglio",
                      data: returnSeries,
                      borderColor: colorBuy,
                      borderWidth: 2,
                      tension: 0.2,
                      pointRadius: 0,
                      fill: {
                        target: { value: 0 },
                        above: "rgba(0,255,136,0.10)",
                        below: "rgba(255,59,59,0.10)",
                      },
                      segment: {
                        borderColor: (ctx) => {
                          const isToday = performance.today_index != null && ctx.p0DataIndex >= performance.today_index - 1;
                          const base = ctx.p1.parsed.y >= 0 ? colorBuy : colorSell;
                          return isToday ? `${base}99` : base;
                        },
                        borderDash: (ctx) =>
                          performance.today_index != null && ctx.p0DataIndex >= performance.today_index - 1
                            ? [6, 4] : undefined,
                      },
                      spanGaps: true,
                      datalabels: { display: false },
                    },
                    {
                      label: "S&P500",
                      data: performance.sp500_return || [],
                      borderColor: "#f59e0b",
                      borderWidth: 1.5,
                      borderDash: [5, 3],
                      tension: 0.2,
                      pointRadius: 0,
                      spanGaps: true,
                      datalabels: { display: false },
                    },
                    {
                      data: performance.dates.map(() => 0),
                      borderColor: "rgba(255,255,255,0.2)",
                      borderWidth: 1,
                      borderDash: [4, 4],
                      pointRadius: 0,
                      datalabels: { display: false },
                    },
                  ],
                }}
                options={{
                  ...baseChartOptions,
                  plugins: {
                    ...baseChartOptions.plugins,
                    legend: {
                      display: true,
                      position: "top",
                      labels: {
                        color: "white",
                        filter: (item) => item.datasetIndex < 2,
                        boxWidth: 20,
                        font: { size: 11 },
                      },
                    },
                    tooltip: {
                      callbacks: {
                        label: (ctx) => {
                          if (ctx.datasetIndex === 2) return null;
                          const v = ctx.parsed.y;
                          const name = ctx.dataset.label || "";
                          return v != null
                            ? `${name}: ${v >= 0 ? "+" : ""}${v.toFixed(2)}%`
                            : null;
                        },
                      },
                    },
                  },
                  scales: {
                    ...baseChartOptions.scales,
                    y: {
                      ...baseChartOptions.scales.y,
                      ticks: {
                        color: "white",
                        callback: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`,
                      },
                    },
                  },
                }}
              />
            </div>
          </>
        );
      })()}

      {/* ================= SHARPE RATIO ================= */}
      {performance && (() => {
        const total  = performance.total || [];
        const dates  = performance.dates || [];

        // Rendimenti giornalieri r[i] = (V[i] - V[i-1]) / V[i-1]
        // retDates[i] corrisponde a dates[i+1]
        const retValues = [];
        const retDates  = [];
        for (let i = 1; i < total.length; i++) {
          if (total[i] != null && total[i - 1] != null && total[i - 1] !== 0) {
            retValues.push((total[i] - total[i - 1]) / total[i - 1]);
            retDates.push(dates[i]);
          }
        }

        const computeSharpe = (rets) => {
          if (rets.length < 10) return null;
          const mean = rets.reduce((s, r) => s + r, 0) / rets.length;
          const variance = rets.reduce((s, r) => s + (r - mean) ** 2, 0) / (rets.length - 1);
          const std = Math.sqrt(variance);
          if (std === 0) return null;
          const rfDaily = Math.pow(1.04, 1 / 252) - 1;
          return (mean - rfDaily) / std * Math.sqrt(252);
        };

        // Valori statici per finestra — mostrati solo se il periodo ha dati distinti
        // (se slice(-N) == tutto l'array, la finestra non è significativa)
        const n = retValues.length;
        const windows = [
          { label: "Globale",  rets: retValues,             minDays: 10  },
          { label: "12 mesi",  rets: retValues.slice(-252), minDays: 126 },
          { label: "6 mesi",   rets: retValues.slice(-126), minDays: 63  },
          { label: "3 mesi",   rets: retValues.slice(-63),  minDays: 20  },
        ]
          .filter(w => (w.rets.length >= w.minDays && w.rets.length < n * 0.99) || w.label === "Globale")
          .map(w => ({ label: w.label, value: computeSharpe(w.rets) }))
          .filter(w => w.value !== null);

        // Sharpe rolling a 30 giorni su tutto lo storico
        const ROLL = 30;
        const rollingDates  = [];
        const rollingValues = [];
        for (let i = ROLL - 1; i < retValues.length; i++) {
          const slice = retValues.slice(i - ROLL + 1, i + 1);
          const s = computeSharpe(slice);
          rollingDates.push(retDates[i]);
          rollingValues.push(s !== null ? parseFloat(s.toFixed(4)) : null);
        }

        const globalVal = computeSharpe(retValues);
        if (globalVal === null && rollingValues.length === 0) return null;

        const sharpeColor = (v) =>
          v == null ? "#667" : v >= 1.5 ? "#00ff88" : v >= 0.5 ? "#ffaa00" : v >= 0 ? "#889" : "#ff5555";

        const qualLabel = (v) =>
          v == null ? "—" : v >= 1.5 ? "Eccellente" : v >= 1 ? "Buono" : v >= 0.5 ? "Accettabile" : v >= 0 ? "Basso" : "Negativo";

        return (
          <div style={{
            backgroundColor: SEMANTIC.backgroundCard,
            borderRadius: 15,
            padding: "24px 28px",
            marginTop: 60,
            marginBottom: 60,
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}>
            {/* ── header ── */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ color: "#aab", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                  Indice di Sharpe del portafoglio
                </div>
                <div style={{ color: "#667", fontSize: 12 }}>
                  Rendimento aggiustato per il rischio · Rf = 4% · Rolling {ROLL}gg
                </div>
              </div>
              {globalVal !== null && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "#667", fontSize: 11, marginBottom: 2 }}>Valore globale</div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: sharpeColor(globalVal), letterSpacing: -0.5, lineHeight: 1 }}>
                    {globalVal.toFixed(1)}
                  </div>
                  <div style={{ color: sharpeColor(globalVal), fontSize: 12, marginTop: 2 }}>
                    {qualLabel(globalVal)}
                  </div>
                </div>
              )}
            </div>

            {/* ── grafico rolling ── */}
            {rollingValues.length > 0 && (
              <div id="sharpe-chart-box" style={{ height: 220, marginBottom: 24 }}>
                <Line
                  data={{
                    labels: rollingDates,
                    datasets: [
                      {
                        label: `Sharpe rolling ${ROLL}gg`,
                        data: rollingValues,
                        borderColor: rollingValues.map(v => sharpeColor(v)),
                        borderWidth: 2,
                        tension: 0.3,
                        pointRadius: 0,
                        spanGaps: true,
                        segment: {
                          borderColor: (ctx) => sharpeColor(ctx.p1.parsed.y),
                        },
                        datalabels: { display: false },
                      },
                      // linee di riferimento
                      { label: "_0",   data: rollingDates.map(() => 0),   borderColor: "rgba(255,255,255,0.15)", borderWidth: 1, borderDash: [4,4], pointRadius: 0, datalabels: { display: false } },
                      { label: "_0.5", data: rollingDates.map(() => 0.5), borderColor: "rgba(255,170,0,0.25)",   borderWidth: 1, borderDash: [4,4], pointRadius: 0, datalabels: { display: false } },
                      { label: "_1.5", data: rollingDates.map(() => 1.5), borderColor: "rgba(0,255,136,0.25)",   borderWidth: 1, borderDash: [4,4], pointRadius: 0, datalabels: { display: false } },
                    ],
                  }}
                  options={{
                    ...baseChartOptions,
                    plugins: {
                      ...baseChartOptions.plugins,
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            if (ctx.dataset.label?.startsWith("_")) return null;
                            const v = ctx.parsed.y;
                            return v != null ? `Sharpe: ${v.toFixed(3)}` : null;
                          },
                        },
                      },
                    },
                    scales: {
                      ...baseChartOptions.scales,
                      y: {
                        ...baseChartOptions.scales.y,
                        ticks: { color: "white", callback: (v) => v.toFixed(2) },
                      },
                    },
                  }}
                />
              </div>
            )}

            {/* ── valori statici per finestra (solo periodi con dati sufficienti) ── */}
            {windows.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
                {windows.map(({ label, value }) => (
                  <div key={label} style={{
                    flex: "1 1 120px",
                    backgroundColor: "#0d0d22",
                    borderRadius: 10,
                    padding: "12px 16px",
                    border: `1px solid ${sharpeColor(value)}33`,
                  }}>
                    <div style={{ color: "#667", fontSize: 11, marginBottom: 4 }}>{label}</div>
                    <div style={{ color: sharpeColor(value), fontSize: 20, fontWeight: 700 }}>
                      {value.toFixed(3)}
                    </div>
                    <div style={{ color: "#556", fontSize: 11 }}>{qualLabel(value)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── legenda soglie ── */}
            <div style={{ display: "flex", gap: 18, marginTop: 16, flexWrap: "wrap" }}>
              {[["≥ 1.5", "#00ff88", "Eccellente"], ["≥ 0.5", "#ffaa00", "Accettabile"], ["≥ 0", "#889", "Basso"], ["< 0", "#ff5555", "Negativo"]].map(([thr, color, lbl]) => (
                <div key={thr} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color }} />
                  <span style={{ color: "#667", fontSize: 11 }}>{thr} {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ================= PORTFOLIOS ================= */}
      {performance && (
        <>
          <h4 className="text-center mb-3">{t("performance.comparison")}</h4>
          <div
            id="comparison-chart-box"
            style={{
              backgroundColor: SEMANTIC.backgroundCard,
              padding: "20px",
              borderRadius: "15px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
              height: "400px",
            }}
          >
            <Line
              data={{
                labels: performance.dates,
                datasets: performance.portfolios.flatMap((p, idx) => {
                  const base = buildPortfolioDataset(
                    p,
                    COLORS[idx % COLORS.length],
                    performance.today_index
                  );
                  const pts = buildEventPoints(p.values, p.events);
                  return [
                    base,
                    buildEventDataset({
                      label: `${p.label} ${t("performance.event_funding")}`,
                      points: pts.filter((pt) => pt.type === "F"),
                      letter: "F",
                      offsetFactor: 1.01,
                    }),
                    buildEventDataset({
                      label: `${p.label} ${t("performance.event_withdraw")}`,
                      points: pts.filter((pt) => pt.type === "W"),
                      letter: "W",
                      offsetFactor: 0.99,
                    }),
                  ];
                }),
              }}
              options={{
                ...baseChartOptions,
                plugins: {
                  ...baseChartOptions.plugins,
                  legend: {
                    ...baseChartOptions.plugins.legend,
                    labels: {
                      filter: (item) =>
                        !item.text.includes(t("performance.event_funding")) &&
                        !item.text.includes(t("performance.event_withdraw")),
                    },
                    onClick: (e, legendItem, legend) => {
                      const chart = legend.chart;
                      const datasetIndex = legendItem.datasetIndex;

                      ChartJS.defaults.plugins.legend.onClick(
                        e,
                        legendItem,
                        legend
                      );

                      const visible = chart.isDatasetVisible(datasetIndex);

                      chart.setDatasetVisibility(datasetIndex + 1, visible);
                      chart.setDatasetVisibility(datasetIndex + 2, visible);

                      chart.update();
                    },
                  },
                },
              }}
            />
          </div>
        </>
      )}

      {/* ================= EXPORT / SEND ================= */}
      {performance && (
        <div className="d-flex justify-content-center gap-3 mt-4 mb-4">
          <button
            className="btn"
            onClick={exportToPDF}
            disabled={exporting || sending}
            style={{
              backgroundColor: SEMANTIC.ctaPrimary,
              color: SEMANTIC.ctaPrimaryText,
              borderRadius: "30px",
              padding: "10px 28px",
              fontWeight: "bold",
              opacity: exporting ? 0.7 : 1,
            }}
          >
            <i className="bi bi-download me-2" />
            {exporting ? "Generazione PDF..." : t("performance.export_dashboard")}
          </button>

          <button
            className="btn"
            onClick={sendDashboard}
            disabled={exporting || sending}
            style={{
              backgroundColor: "transparent",
              color: SEMANTIC.ctaPrimary,
              border: `2px solid ${SEMANTIC.ctaPrimary}`,
              borderRadius: "30px",
              padding: "10px 28px",
              fontWeight: "bold",
              opacity: sending ? 0.7 : 1,
            }}
          >
            <i className="bi bi-envelope me-2" />
            {sending ? "Invio in corso..." : t("performance.send_dashboard")}
          </button>
        </div>
      )}
    </div>
    </>
  );
};

export default Performance;
